/**
 * BalanceGearsIcon — level seesaw with a gear on each side.
 * Artboard: 1024×1024 · viewBox 0 0 24 24 (fixed).
 */
import React from 'react';
import Svg, { Path, Rect } from 'react-native-svg';
import {
  APP_ICON_VIEW_BOX,
  useAppIconProps,
  type AppIconProps,
} from './iconBase';

/** Compact 8-tooth gear with center hub cutout (evenodd). */
function gearPath(cx: number, cy: number, outer = 3.15, inner = 2.35, hub = 1.05): string {
  const teeth = 8;
  const step = (Math.PI * 2) / teeth;
  const parts: string[] = [];
  for (let i = 0; i < teeth; i++) {
    const mid = -Math.PI / 2 + i * step;
    const tooth_half = step * 0.18;
    const gap_half = step * 0.32;
    const a0 = mid - gap_half;
    const a1 = mid - tooth_half;
    const a2 = mid + tooth_half;
    const a3 = mid + gap_half;
    const pt = (r: number, a: number) => `${(cx + r * Math.cos(a)).toFixed(3)},${(cy + r * Math.sin(a)).toFixed(3)}`;
    if (i === 0) {
      parts.push(`M ${pt(inner, a0)}`);
    }
    parts.push(`L ${pt(inner, a1)}`);
    parts.push(`L ${pt(outer, a1)}`);
    parts.push(`L ${pt(outer, a2)}`);
    parts.push(`L ${pt(inner, a2)}`);
    parts.push(`L ${pt(inner, a3)}`);
  }
  parts.push('Z');
  parts.push(`M ${(cx + hub).toFixed(3)} ${cy.toFixed(3)}`);
  parts.push(`A ${hub} ${hub} 0 1 0 ${(cx - hub).toFixed(3)} ${cy.toFixed(3)}`);
  parts.push(`A ${hub} ${hub} 0 1 0 ${(cx + hub).toFixed(3)} ${cy.toFixed(3)}`);
  parts.push('Z');
  return parts.join(' ');
}

export function BalanceGearsIcon(props: AppIconProps) {
  const { size, color, style, accessibilityLabel, testID, ...rest } = useAppIconProps(props);

  return (
    <Svg
      width={size}
      height={size}
      viewBox={APP_ICON_VIEW_BOX}
      style={style}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      {...rest}
    >
      <Path d={gearPath(6.2, 6.15)} fill={color} fillRule="evenodd" />
      <Path d={gearPath(17.8, 6.15)} fill={color} fillRule="evenodd" />
      <Rect x="3.25" y="9.15" width="17.5" height="2.35" rx="1.15" fill={color} />
      <Path
        d="M5.6 21.2 L12 11.15 L18.4 21.2 Z M13.05 12.35 A 1.05 1.05 0 1 0 10.95 12.35 A 1.05 1.05 0 1 0 13.05 12.35 Z"
        fill={color}
        fillRule="evenodd"
      />
    </Svg>
  );
}

export default BalanceGearsIcon;
